import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  AfterViewInit,
  ViewChild,
  HostListener,
  ElementRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { IconValidation, TableColumn, TableStyleConfig, SimpleTableConfig } from '../table/table.interface';
import {
  MultiselectTableComponent,
  MultiselectOption,
} from '../../multiselect-table/multiselect-table.component';
import { TruncateTooltipDirective } from 'src/ui/directives/truncate-tooltip.directive';

@Component({
  selector: 'app-simple-table',
  standalone: true,
  imports: [CommonModule, MultiselectTableComponent, TruncateTooltipDirective],
  templateUrl: './simple-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SimpleTableComponent implements AfterViewInit, OnChanges {
  @Input() columns: TableColumn[] = [];
  @Input() paginatedData: any[] = [];
  @Input() emptyRows: any[] = [];
  @Input() showSorting = true;
  @Input() icon1: string = '';
  @Input() icon2: string = '';
  @Input() icon1Tooltip: string = 'Acción 1';
  @Input() icon2Tooltip: string = 'Acción 2';
  @Input() icon1Validation?: IconValidation;
  @Input() icon2Validation?: IconValidation;
  @Input() showToggle = false;
  @Input() showHideOption = false;
  @Input() toggleText: string = 'Acciones';
  @Input() sortColumn: () => string | null = () => null;
  @Input() sortDirection: () => 'asc' | 'desc' = () => 'asc';
  @Input() resultsNotFoundText: string = '';
  @Input() isFirstColumnDark: boolean = false;
  @Input() enableRowClick: boolean = false;
  @Input() showHeaderFilter: boolean = true;
  @Input() showMenuThreeDots: boolean = false;
  @Input() enableInternalScroll: boolean = false;
  @Input() actionColumnClass?: string;
  @Input() compactActionColumn: boolean = false;
  @Input() actionRowHeight: string = 'h-[52px]';
  @Input() theadHeight: string = 'h-[36px]';
  @Input() rowHeight: string = 'h-[42px]';
  @Input() styleConfig?: TableStyleConfig;
  @Input() tableConfig?: SimpleTableConfig;
  @Input() icon1Class: string = 'fi-rr-pencil';
  @Input() icon2Class: string = 'fi-rr-trash';
  @Input() allData: any[] = [];
  @Input() headerStyle?: Record<string, string>;
  @Input() zebraStyle?: Record<string, string>;
  /**
   * PW-116 audit gap UI-09 — cuando se define, restringe las opciones del menú
   * de 3 puntos (⋮) a solo las incluidas. Si se omite, se mantienen todas las
   * opciones legacy (duplicate/edit/hide/delete) para no romper consumidores
   * existentes de app-simple-table.
   */
  @Input() menuActions?: Array<'edit' | 'hide' | 'delete' | 'duplicate'>;
  @Output() iconAction1 = new EventEmitter<any>();
  @Output() iconAction2 = new EventEmitter<any>();
  @Output() toggleAction = new EventEmitter<{ row: any; checked: boolean }>();
  @Output() sortChange = new EventEmitter<{
    column: string;
    direction: 'asc' | 'desc';
  }>();
  @Output() rowClick = new EventEmitter<any>();
  @Output() menuAction = new EventEmitter<{ action: string; row: any }>();
  @Output() filterChange = new EventEmitter<{
    columnKey: string;
    selectedOptions: MultiselectOption[];
  }>();
  @Output() handleToggleShow = new EventEmitter<any>();

  @ViewChild('tableContainer', { static: true })
  tableContainer!: ElementRef<HTMLElement>;

  get headerTextStyle(): Record<string, string> | undefined {
    const color = this.headerStyle?.['color'];
    return color ? { color } : undefined;
  }

  get defaultHeaderTextClass(): string {
    return this.headerTextStyle ? '' : 'text-text-primary';
  }

  showColumnShadow = false;
  openMenuIndex: number | null = null;
  menuPosition = { top: 0, left: 0 };

  private readonly filterOptionsCache: Map<string, MultiselectOption[]> = new Map();
  private readonly activeFilters: Map<string, Set<string | number>> = new Map();

  // Getters para usar tableConfig si existe, sino styleConfig, sino los inputs individuales
  get effectiveTheadHeight(): string {
    return this.tableConfig?.theadHeight ?? this.styleConfig?.theadHeight ?? this.theadHeight;
  }

  get effectiveRowHeight(): string {
    return this.tableConfig?.rowHeight ?? this.styleConfig?.rowHeight ?? this.rowHeight;
  }

  get effectiveActionRowHeight(): string {
    return this.tableConfig?.actionRowHeight ?? this.styleConfig?.actionRowHeight ?? this.actionRowHeight;
  }

  get effectiveActionColumnClass(): string | undefined {
    return this.tableConfig?.actionColumnClass ?? this.styleConfig?.actionColumnClass ?? this.actionColumnClass;
  }

  get effectiveIcon1Class(): string {
    return this.tableConfig?.icon1Class ?? this.styleConfig?.icon1Class ?? this.icon1Class;
  }

  get effectiveIcon2Class(): string {
    return this.tableConfig?.icon2Class ?? this.styleConfig?.icon2Class ?? this.icon2Class;
  }

  constructor(private readonly cdr: ChangeDetectorRef) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['paginatedData'] && !changes['paginatedData'].firstChange) {
      this.regenerateFilterOptions();
      this.cdr.markForCheck();
    }
  }

  ngAfterViewInit() {
    this.checkScroll();
  }

  @HostListener('window:resize')
  onResize() {
    this.checkScroll();
  }

  onTableScroll() {
    this.checkScroll();
  }

  private checkScroll() {
    const el = this.tableContainer?.nativeElement;
    if (!el) {
      this.showColumnShadow = false;
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = el;
    this.showColumnShadow =
      scrollWidth > clientWidth && scrollLeft < scrollWidth - clientWidth - 1;
  }

  validateIcon(row: any): boolean {
    if (!this.icon1Validation) return false;
    const invalidValues = this.icon1Validation.invalidValues || [];
    return invalidValues.includes(row[this.icon1Validation.columnKey]);
  }

  validateIcon2(row: any): boolean {
    if (!this.icon2Validation) return false;
    const invalidValues = this.icon2Validation.invalidValues || [];
    return invalidValues.includes(row[this.icon2Validation.columnKey]);
  }

  isIcon1Disabled(row: any): boolean {
    return this.validateIcon(row) || row.icon1IsLoading || (row.isEditDisabled && this.icon1.includes('edit'));
  }

  isIcon2Disabled(row: any): boolean {
    return this.validateIcon2(row) || (row.isEditDisabled && this.icon2.includes('edit'));
  }

  icon1TitleFor(row: any): string {
    return this.isIcon1Disabled(row) ? (this.icon1Validation?.disabledTooltip ?? this.icon1Tooltip) : this.icon1Tooltip;
  }

  icon2TitleFor(row: any): string {
    return this.isIcon2Disabled(row) ? (this.icon2Validation?.disabledTooltip ?? this.icon2Tooltip) : this.icon2Tooltip;
  }

  onSortToggle(columnKey: string) {
    const currentDirection =
      this.sortColumn() === columnKey ? this.sortDirection() : 'desc';
    const newDirection: 'asc' | 'desc' =
      currentDirection === 'asc' ? 'desc' : 'asc';
    this.sortChange.emit({ column: columnKey, direction: newDirection });
  }

  onSort(columnKey: string, direction: 'asc' | 'desc') {
    this.sortChange.emit({ column: columnKey, direction });
  }

  toggleRow(row: any) {
    row.toggleActive = !row.toggleActive;
    this.toggleAction.emit({ row, checked: row.toggleActive });
  }

  getStatusClasses(status: string): object {
    if (!status) {
      return {};
    }

    const lowerCaseStatus = status.toLowerCase();

    const greenStatus = ['activo', 'disponible'];
    const redStatus = ['inactivo', 'vencido'];
    const yellowStatus = ['pendiente'];
    const secondaryStatus = ['borrador', 'test'];

    if (greenStatus.includes(lowerCaseStatus)) {
      return { 'bg-chip-green': true, 'text-text-primary': true };
    }

    if (redStatus.includes(lowerCaseStatus)) {
      return { 'bg-chip-red': true, 'text-text-primary': true };
    }

    if (yellowStatus.includes(lowerCaseStatus)) {
      return { 'bg-chip-yellow': true, 'text-text-primary': true };
    }

    if (secondaryStatus.includes(lowerCaseStatus)) {
      return { 'bg-secundary-b1200': true, 'text-text-primary': true };
    }

    return { 'bg-gray-200': true, 'text-text-primary': true };
  }

  onRowClick(row: any, event: Event) {
    if (this.enableRowClick) {
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'BUTTON' ||
        target.tagName === 'INPUT' ||
        target.closest('button')
      ) {
        return;
      }
      this.rowClick.emit(row);
    }
  }

  getRowCursorStyle(): string {
    return this.enableRowClick ? 'cursor-pointer' : '';
  }

  toggleMenu(index: number, event: Event): void {
    event.stopPropagation();

    if (this.openMenuIndex === index) {
      this.openMenuIndex = null;
    } else {
      const button = event.target as HTMLElement;
      const rect = button.getBoundingClientRect();

      this.menuPosition = {
        top: rect.bottom + 8,
        left: rect.right - 120,
      };

      this.openMenuIndex = index;
    }
  }

  onMenuAction(action: string, row: any): void {
    this.openMenuIndex = null;
    this.menuAction.emit({ action, row });
  }

  @HostListener('document:click')
  closeMenu(): void {
    this.openMenuIndex = null;
  }

  getFilterOptions(columnKey: string): MultiselectOption[] {
      if (this.filterOptionsCache.has(columnKey)) {
        return this.filterOptionsCache.get(columnKey)!;
      }
  
      const dataSource = this.allData.length > 0 ? this.allData : this.paginatedData;
  
      if (!dataSource || dataSource.length === 0) {
        return [];
      }
  
      const uniqueValues = new Set<string | number>();
      const optionsFilter: MultiselectOption[] = [];
      const activeValues = this.activeFilters.get(columnKey) || new Set();
  
      for (const row of dataSource) {
        const value = row[columnKey];
        if (value !== undefined && value !== null && !uniqueValues.has(value)) {
          uniqueValues.add(value);
          optionsFilter.push({
            label: value.toString(),
            value: value,
            active: activeValues.has(value),
          });
        }
      }
  
      this.filterOptionsCache.set(columnKey, optionsFilter);
      return optionsFilter;
    }
    
  onFilterChange(columnKey: string, selectedOptions: MultiselectOption[]) {
    const activeValues = new Set(selectedOptions.map(opt => opt.value));
    this.activeFilters.set(columnKey, activeValues);

    const cachedOptions = this.filterOptionsCache.get(columnKey);
    if (cachedOptions) {
      cachedOptions.forEach(opt => {
        opt.active = activeValues.has(opt.value);
      });
    }

    this.filterChange.emit({ columnKey, selectedOptions });
  }

  private regenerateFilterOptions(): void {
    this.filterOptionsCache.forEach((_, columnKey) => {
      this.filterOptionsCache.delete(columnKey);
    });
  }

  clearFilterCache() {
    this.filterOptionsCache.clear();
    this.activeFilters.clear();
  }

  onToggleShow(row: any): void {
    this.handleToggleShow.emit(row);
  }

  showMenuAction(action: 'edit' | 'hide' | 'delete' | 'duplicate'): boolean {
    return !this.menuActions || this.menuActions.includes(action);
  }

  showHideMenuItem(row: any): boolean {
    if (!this.menuActions) {
      return !!row?.isUsed && !!row?.visible;
    }
    return this.showMenuAction('hide') && !!row?.visible;
  }

  showDeleteMenuItem(row: any): boolean {
    if (!this.menuActions) {
      return !row?.isUsed && !!row?.visible;
    }
    return this.showMenuAction('delete') && !row?.isUsed && !!row?.visible;
  }
}